import React from 'react';
import { Card, Typography, Table, Divider, Empty, Tag } from 'antd';
import { KeyOutlined, SearchOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';

const { Paragraph, Text } = Typography;

interface KeywordAnalysisProps {
  data: any;
  type: string;
}

interface KeywordCountResponse {
  org_name: string;
  keyword_count: number;
}

interface TermOrgCountResponse {
  term: string;
  org_count: number;
  org_list: string;
}

interface FrequentKeywordResponse {
  org_name: string;
  most_frequent_keyword: string;
  usage_count: number;
}

interface RepeatKeywordResponse {
  org_name: string;
  request_date: string;
  keyword: string;
  usage_count: number;
}

const KeywordAnalysis: React.FC<KeywordAnalysisProps> = ({ data, type }) => {
  if (!data || !data.data) {
    return <Empty description="無可用數據" />;
  }

  // 顯示時間範圍
  const renderTimeRange = () => {
    if (!data.time_range) return null;
    
    const { start_date, end_date } = data.time_range;
    return (
      <Text type="secondary">
        查詢時間範圍: {start_date} ~ {end_date || '今天'}
      </Text>
    );
  };

  // 關鍵詞數量
  const renderKeywordCount = () => {
    // 確保數據格式正確
    const processedData = data.data && Array.isArray(data.data) 
      ? data.data.map((item: any, index: number) => ({
          key: `keyword-count-${index}`,
          org_name: item.org_name || '未知組織',
          keyword_count: item.keyword_count || 0
        }))
      : [];
    
    const columns: ColumnsType<KeywordCountResponse> = [
      {
        title: '組織名稱',
        dataIndex: 'org_name',
        key: 'org_name',
        sorter: (a, b) => (a.org_name || '').localeCompare(b.org_name || ''),
        render: (value) => value || '未知組織',
      },
      {
        title: '不同關鍵詞數量',
        dataIndex: 'keyword_count',
        key: 'keyword_count',
        sorter: (a, b) => (a.keyword_count || 0) - (b.keyword_count || 0),
        render: (value) => value || 0,
      }
    ];

    return (
      <>
        <Paragraph>此報表顯示各組織使用過的不同關鍵詞數量。</Paragraph>
        {renderTimeRange()}
        <Divider />
        <Table 
          dataSource={processedData} 
          columns={columns} 
          rowKey="key"
          pagination={{ pageSize: 10 }}
        />
      </>
    );
  };

  // 關鍵詞組織使用情況
  const renderTermOrgCount = () => {
    // 確保數據格式正確
    const processedData = data.data && Array.isArray(data.data) 
      ? data.data.map((item: any, index: number) => ({
          key: `term-org-${index}`,
          term: item.term || '未知關鍵詞',
          org_count: item.org_count || 0,
          org_list: item.org_list || ''
        }))
      : [];
    
    const columns: ColumnsType<TermOrgCountResponse> = [
      {
        title: '關鍵詞',
        dataIndex: 'term',
        key: 'term',
        width: 200,
        render: (text) => (
          <div style={{ 
            wordBreak: 'break-all', 
            whiteSpace: 'normal',
            lineHeight: '1.4'
          }}>
            <Tag color="blue" style={{ 
              display: 'inline-block',
              maxWidth: '100%',
              whiteSpace: 'normal',
              wordBreak: 'break-all',
              height: 'auto',
              lineHeight: '1.4',
              padding: '4px 8px'
            }}>
              <KeyOutlined style={{ marginRight: 4 }} />
              {text || '未知關鍵詞'}
            </Tag>
          </div>
        ),
        sorter: (a, b) => (a.term || '').localeCompare(b.term || ''),
      },
      {
        title: '使用該關鍵詞的組織數量',
        dataIndex: 'org_count',
        key: 'org_count',
        width: 120,
        sorter: (a, b) => (a.org_count || 0) - (b.org_count || 0),
        render: (value) => value || 0,
      },
      {
        title: '使用組織列表',
        dataIndex: 'org_list',
        key: 'org_list',
        width: 500,
        render: (text) => (
          <div style={{ 
            wordBreak: 'break-all', 
            whiteSpace: 'normal',
            lineHeight: '1.4'
          }}>
            {text || '無'}
          </div>
        ),
        sorter: (a, b) => (a.org_list || '').localeCompare(b.org_list || ''),
      }
    ];

    return (
      <>
        <Paragraph>此報表顯示每個關鍵詞被多少個組織使用過。</Paragraph>
        {renderTimeRange()}
        <Divider />
        <Table 
          dataSource={processedData} 
          columns={columns} 
          rowKey="key"
          pagination={{ pageSize: 10 }}
          scroll={{ x: 900 }}
        />
      </>
    );
  };

  // 用戶搜尋關鍵詞紀錄
  const renderMostFrequentKeyword = () => {
    // 確保數據格式正確
    const processedData = data.data && Array.isArray(data.data) 
      ? data.data.map((item: any, index: number) => ({
          key: `most-frequent-${index}`,
          org_name: item.org_name || '未知組織',
          most_frequent_keyword: item.most_frequent_keyword || '未知關鍵詞',
          usage_count: item.usage_count || 0
        }))
      : [];
    
    const columns: ColumnsType<FrequentKeywordResponse> = [
      {
        title: '組織名稱',
        dataIndex: 'org_name',
        key: 'org_name',
        sorter: (a, b) => (a.org_name || '').localeCompare(b.org_name || ''),
        render: (value) => value || '未知組織',
      },
      {
        title: '最常使用的關鍵詞',
        dataIndex: 'most_frequent_keyword',
        key: 'most_frequent_keyword',
        width: 300,
        render: (text) => (
          <div style={{ 
            wordBreak: 'break-all', 
            whiteSpace: 'normal',
            lineHeight: '1.4'
          }}>
            <Tag color="blue" style={{ 
              display: 'inline-block',
              maxWidth: '100%',
              whiteSpace: 'normal',
              wordBreak: 'break-all',
              height: 'auto',
              lineHeight: '1.4',
              padding: '4px 8px'
            }}>
              <SearchOutlined style={{ marginRight: 4 }} />
              {text || '未知關鍵詞'}
            </Tag>
          </div>
        ),
        sorter: (a, b) => (a.most_frequent_keyword || '').localeCompare(b.most_frequent_keyword || ''),
      },
      {
        title: '使用次數',
        dataIndex: 'usage_count',
        key: 'usage_count',
        sorter: (a, b) => (a.usage_count || 0) - (b.usage_count || 0),
        render: (value) => value || 0,
      }
    ];

    return (
      <>
        <Paragraph>此報表顯示各組織搜尋關鍵詞的使用紀錄統計。</Paragraph>
        {renderTimeRange()}
        <Divider />
        <Table 
          dataSource={processedData} 
          columns={columns} 
          rowKey="key"
          pagination={{ pageSize: 10 }}
          scroll={{ x: 700 }}
        />
      </>
    );
  };

  // 重複關鍵詞分析
  const renderRepeatKeyword = () => {
    // 確保數據格式正確
    const processedData = data.data && Array.isArray(data.data) 
      ? data.data.map((item: any, index: number) => ({
          key: `repeat-keyword-${index}`,
          org_name: item.org_name || '未知組織',
          request_date: item.request_date || '未知日期',
          keyword: item.keyword || '未知關鍵詞',
          usage_count: item.usage_count || 0
        }))
      : [];
    
    const columns: ColumnsType<RepeatKeywordResponse> = [
      {
        title: '組織名稱',
        dataIndex: 'org_name',
        key: 'org_name',
        sorter: (a, b) => (a.org_name || '').localeCompare(b.org_name || ''),
        render: (value) => value || '未知組織',
      },
      {
        title: '查詢日期',
        dataIndex: 'request_date',
        key: 'request_date',
        sorter: (a, b) => (a.request_date || '').localeCompare(b.request_date || ''),
        render: (value) => value || '未知日期',
      },
      {
        title: '關鍵詞',
        dataIndex: 'keyword',
        key: 'keyword',
        width: 300,
        render: (text) => (
          <div style={{ 
            wordBreak: 'break-all', 
            whiteSpace: 'normal',
            lineHeight: '1.4'
          }}>
            <Tag color="blue" style={{ 
              display: 'inline-block',
              maxWidth: '100%',
              whiteSpace: 'normal',
              wordBreak: 'break-all',
              height: 'auto',
              lineHeight: '1.4',
              padding: '4px 8px'
            }}>
              <SearchOutlined style={{ marginRight: 4 }} />
              {text || '未知關鍵詞'}
            </Tag>
          </div>
        ),
        sorter: (a, b) => (a.keyword || '').localeCompare(b.keyword || ''),
      },
      {
        title: '重複查詢次數',
        dataIndex: 'usage_count',
        key: 'usage_count',
        sorter: (a, b) => (a.usage_count || 0) - (b.usage_count || 0),
        render: (value) => value || 0,
      }
    ];

    return (
      <>
        <Paragraph>此報表顯示同一組織、同一天、同一關鍵詞被重複查詢的情況（次數 &gt; 1）。</Paragraph>
        {renderTimeRange()}
        <Divider />
        <Table 
          dataSource={processedData} 
          columns={columns} 
          rowKey="key"
          pagination={{ pageSize: 10 }}
          scroll={{ x: 800 }}
        />
      </>
    );
  };

  // 根據類型渲染不同的內容
  const renderContent = () => {
    switch(type) {
      case 'keyword_count':
        return renderKeywordCount();
      case 'term_org_count':
        return renderTermOrgCount();
      case 'most_frequent_keyword':
        return renderMostFrequentKeyword();
      case 'repeat_keyword':
        return renderRepeatKeyword();
      default:
        return <Empty description="未知的分析類型" />;
    }
  };

  return (
    <Card>
      {renderContent()}
    </Card>
  );
};

export default KeywordAnalysis; 